import { t } from '@jotforminc/translation';
import FileValidator from './file-validator';

import '../styles/jotform-image.css';

class JotformImage {
  modalActive = false;

  dropzoneActive = false;

  dropZoneEnabled = true;

  inputTextEnabled = true;

  MAX_NUM_OF_FILES = 6;

  index = 0;

  constructor(quill, options = {}) {
    this.quill = quill;
    this.options = options;
    this.fileValidator = new FileValidator({ maxFileSize: this.options.maxFileSize });
    this.editorContainerId = `#${this.quill.container.id}-container`;

    this.renderElements();
    this.renderToolbarIcon();
  }

  renderElements() {
    this.jfImageBtnEl = document.querySelector(`${this.editorContainerId} .ql-jotformImage`);
    this.jfImageBtnEl.addEventListener('click', this.openModal.bind(this));
  }

  getElRefs() {
    this.closeEl = document.querySelector('.ql-modal-close');
    this.dialogEl = document.querySelector('.ql-modal-dialog');
    this.modalEl = document.querySelector('.ql-modal');
    this.cancelEl = document.querySelector('.ql-modal-cancel');
    this.btnAddEl = document.querySelector('.ql-modal-add');
    this.btnDropZoneEl = document.querySelector('.ql-modal-btn-dropzone');
    this.inputFileEl = document.querySelector('.ql-modal-input-file');
    this.inputTextEl = document.querySelector('.ql-modal-input');
    this.thumbnailsGroupEl = document.querySelector('.ql-modal-group-thumbnails');
    this.errorsEl = document.querySelector('.ql-modal-errors');
    this.errorCountEl = document.querySelector('.ql-modal-error.count');
    this.errorTypeEl = document.querySelector('.ql-modal-error.type');
    this.errorSizeEl = document.querySelector('.ql-modal-error.size');

    this.thumbnailsGroupEl.innerHTML = this.getLoaderTemplate();
    this.thumbnailLoaderEl = document.querySelector('.ql-modal-thumbnail-loader');
  }

  bindEvents() {
    this.closeEl.addEventListener('click', this.closeModal.bind(this));
    this.cancelEl.addEventListener('click', this.closeModal.bind(this));
    this.modalEl.addEventListener('click', this.closeModal.bind(this));
    this.dialogEl.addEventListener('click', e => e.stopPropagation());
    document.addEventListener('keyup', this.handleKeyUp.bind(this));

    this.inputFileEl.addEventListener('dragenter', this.handleDragEnter.bind(this));
    this.inputFileEl.addEventListener('dragleave', this.handleDragLeave.bind(this));

    this.inputTextEl.addEventListener('keyup', this.handleInputTextKeyUp.bind(this));

    this.btnAddEl.addEventListener('click', this.addImage.bind(this));
    this.thumbnailsGroupEl.addEventListener('click', this.removeThumbnail.bind(this));
    this.thumbnailsGroupEl.addEventListener('thumbnailschange', this.handleThumbnailsChange.bind(this));

    this.inputFileEl.addEventListener('change', this.handleInputFileChange.bind(this));

    this.inputFileEl.addEventListener('dragover', e => e.preventDefault());
    this.inputFileEl.addEventListener('drop', this.handleInputFileDrop.bind(this));
  }

  handleThumbnailsChange(e) {
    const { thumbnailsCount } = e.detail;
    if (thumbnailsCount >= this.MAX_NUM_OF_FILES && this.dropZoneEnabled) {
      this.toggleDropZone(false);
      return;
    }

    if (thumbnailsCount < this.MAX_NUM_OF_FILES && !this.dropZoneEnabled) {
      this.toggleDropZone(true);
    }

    if (thumbnailsCount === 0 && !this.inputTextEnabled) {
      this.toggleInputText(true);
    }
  }

  handleInputFileDrop(e) {
    e.preventDefault();
    if (e.dataTransfer && e.dataTransfer.files) this.readFiles(e.dataTransfer.files);

    this.btnDropZoneEl.removeAttribute('data-state');
  }

  getNumberOfThumbnails() {
    if (!this.thumbnailsGroupEl) {
      return 0;
    }

    return this.thumbnailsGroupEl.children.length - 1;
  }

  handleInputFileChange() {
    if (this.inputFileEl.files) this.readFiles(this.inputFileEl.files);
  }

  handleInputTextKeyUp(e) {
    this.toggleDropZone(!e.target.value);
  }

  handleDragEnter() {
    this.btnDropZoneEl.setAttribute('data-state', 'active');
    this.dropzoneActive = true;
  }

  handleDragLeave() {
    this.btnDropZoneEl.removeAttribute('data-state');
    this.dropzoneActive = false;
  }

  handleKeyUp(e) {
    if (e.keyCode === 27 && this.modalActive) {
      this.closeModal();
    }
  }

  dispatchThumbnailsChangeEvent() {
    const eventData = { thumbnailsCount: this.getNumberOfThumbnails() };
    // eslint-disable-next-line no-undef
    const thumbnailsChange = new CustomEvent('thumbnailschange', { detail: eventData });
    this.thumbnailsGroupEl.dispatchEvent(thumbnailsChange);
  }

  readFiles(files) {
    // eslint-disable-next-line no-undef
    const fragment = document.createDocumentFragment();
    const promises = [];
    let filesCount = this.getNumberOfThumbnails();

    [].forEach.call(files, file => {
      const { type, size, name } = file;
      const { valid, readable } = this.fileValidator.applyAllValidations(filesCount, type, size);
      if (!valid) {
        this.renderError(readable, name);
        return;
      }

      // at least one file can pass checks then show the loader.
      if (this.inputTextEnabled) this.toggleInputText(false);
      this.toggleThumbnailLoader(true);

      // eslint-disable-next-line no-undef
      const reader = new FileReader();
      reader.readAsDataURL(file);
      filesCount++;
      // eslint-disable-next-line no-loop-func
      const readerPromise = new Promise((resolve, reject) => {
        reader.onload = ev => {
          const thumbnailEl = this.createThumbnailElement(ev.target.result, type, name);
          fragment.appendChild(thumbnailEl);
          resolve();
        };

        reader.onerror = () => {
          reader.abort();
          reject(name);
        };
      }).catch(fileName => {
        this.renderError('Cannot read file.', fileName);
      });
      promises.push(readerPromise);
    });

    Promise.all(promises).then(() => {
      this.toggleThumbnailLoader(false);
      this.thumbnailsGroupEl.appendChild(fragment);
      this.inputFileEl.value = null;
      this.dispatchThumbnailsChangeEvent();
    });
  }

  addImage() {
    if (this.inputTextEl.value) {
      const imgSrc = this.inputTextEl.value.trim();

      this.quill.insertEmbed(this.index, 'image', imgSrc, 'user');
      this.quill.setSelection(this.index + 1, 'user');
    } else {
      const imageEls = document.querySelectorAll('.ql-modal-thumbnail-img');
      [].forEach.call(imageEls, el => {
        const dataURL = el.getAttribute('src');
        const type = el.getAttribute('data-type');
        const name = el.getAttribute('data-name');
        if (typeof this.options.onImageAdd === 'function') {
          this.options.onImageAdd(this.quill, dataURL, type, name, this.index);
        } else {
          this.quill.insertEmbed(this.index, 'image', dataURL, 'user');
          this.quill.setSelection(this.index + 1, 'user');
        }
      });
    }
    this.closeModal();
  }

  toggleDropZone(shouldEnable) {
    if (shouldEnable) {
      this.btnDropZoneEl.removeAttribute('disabled', '');
      this.inputFileEl.removeAttribute('disabled', '');
      this.dropZoneEnabled = true;
    } else {
      this.btnDropZoneEl.setAttribute('disabled', '');
      this.inputFileEl.setAttribute('disabled', '');
      this.dropZoneEnabled = false;
    }
  }

  toggleInputText(shouldEnable) {
    if (shouldEnable) {
      this.inputTextEl.removeAttribute('disabled', '');
      this.inputTextEnabled = true;
    } else {
      this.inputTextEl.setAttribute('disabled', '');
      this.inputTextEnabled = false;
    }
  }

  toggleThumbnailLoader(shouldShow) {
    if (shouldShow) {
      this.thumbnailLoaderEl.setAttribute('data-state', 'show');
    } else {
      // setTimeout needed for IE, otherwise can't remove the attribute
      setTimeout(() => this.thumbnailLoaderEl.removeAttribute('data-state'), 10);
    }
  }

  removeThumbnail(e) {
    if (e.target.hasAttribute('data-action') && e.target.getAttribute('data-action') === 'thumbnail-remove') {
      const elementToRemove = e.target.parentElement;
      elementToRemove.parentElement.removeChild(elementToRemove);
    }

    this.dispatchThumbnailsChangeEvent();
  }

  removeAllThumbnails() {
    this.thumbnailsGroupEl.innerHTML = this.getLoaderTemplate();
    this.thumbnailLoaderEl = this.thumbnailsGroupEl.querySelector('.ql-modal-thumbnail-loader');
    this.dispatchThumbnailsChangeEvent();
  }

  checkClassName(el, classNameToLook) {
    const re = new RegExp(classNameToLook);
    return re.test(el.className);
  }

  openModal() {
    this.renderModal();
    this.getElRefs();
    this.bindEvents();
    setTimeout(() => {
      document.body.setAttribute('data-state', 'modalActive');
      this.backdropEl.setAttribute('data-state', 'open');
      this.modalEl.setAttribute('data-state', 'open');
      this.modalActive = true;

      this.index = this.quill.getSelection()
        ? this.quill.getSelection().index
        : this.quill.getLength() - 1;

      this.inputTextEl.focus();
    }, 50);
  }

  removeModal() {
    // remove items from dom because multiple editor causuing multiple modals.
    this.closeEl?.remove();
    this.dialogEl?.remove();
    this.modalEl?.remove();
    this.cancelEl?.remove();
    this.btnAddEl?.remove();
    this.btnDropZoneEl?.remove();
    this.inputFileEl?.remove();
    this.inputTextEl?.remove();
    this.thumbnailsGroupEl?.remove();
    this.errorsEl?.remove();
    this.errorCountEl?.remove();
    this.errorTypeEl?.remove();
    this.errorSizeEl?.remove();

    this.thumbnailsGroupEl?.remove();
    this.thumbnailLoaderEl?.remove();
  }

  closeModal() {
    document.body.removeAttribute('data-state');
    this.backdropEl.setAttribute('data-state', 'closed');
    this.modalEl.setAttribute('data-state', 'closed');
    this.modalActive = false;
    this.inputTextEl.value = '';
    if (!this.dropZoneEnabled) this.toggleDropZone(true);
    if (!this.inputTextEnabled) this.toggleInputText(true);
    this.removeAllThumbnails();
    this.removeModal();
  }

  renderModal() {
    this.modalEl = this.createModalEl('div', 'ql-modal', document.body);
    this.modalEl.setAttribute('data-state', 'closed');
    const dialogEl = this.createModalEl('div', 'ql-modal-dialog', this.modalEl);
    dialogEl.setAttribute('tabindex', 1);
    const contentEl = this.createModalEl('div', 'ql-modal-content', dialogEl);
    contentEl.innerHTML = this.getModalContentTemplate();
    this.backdropEl = this.createModalEl('div', 'ql-modal-backdrop', document.body);
    this.backdropEl.setAttribute('data-state', 'closed');
  }

  createModalEl(elType, className, containerEl) {
    const el = document.createElement(elType);
    el.classList.add(className);
    containerEl.appendChild(el);

    return el;
  }

  renderError(message, fileName) {
    const ERROR_DURATION = 5000;
    const errorEl = document.createElement('li');
    errorEl.classList.add('ql-modal-error');
    errorEl.innerHTML = `${message} <br> File Name: ${fileName}`;
    this.errorsEl.appendChild(errorEl);

    errorEl.setAttribute('data-state', 'active');
    this.errorsEl.setAttribute('data-state', 'active');

    setTimeout(() => {
      errorEl.parentElement.removeChild(errorEl);
      this.errorsEl.removeAttribute('data-state');
    }, ERROR_DURATION);
  }

  renderToolbarIcon() {
    const iconSVG = `
    <svg viewBox="0 0 18 18"> 
      <rect class="ql-stroke" height="10" width="12" x="3" y="4"></rect> 
      <circle class="ql-fill" cx="6" cy="7" r="1"></circle> 
      <polyline class="ql-even ql-fill" points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12"></polyline>
    </svg>`;

    this.jfImageBtnEl.innerHTML = iconSVG;
  }

  createThumbnailElement(src, type, name) {
    const template = `
      <figure class="ql-modal-thumbnail">
        <img class="ql-modal-thumbnail-img" draggable="false">
        <span class="ql-modal-thumbnail-remove" data-action="thumbnail-remove"></span>
      </figure>`;

    const tempEl = document.createElement('div');
    tempEl.innerHTML = template;
    const imgEl = tempEl.querySelector('.ql-modal-thumbnail-img');
    imgEl.setAttribute('src', src);
    imgEl.setAttribute('data-type', type);
    imgEl.setAttribute('data-name', name);

    return tempEl.firstElementChild;
  }

  getLoaderTemplate() {
    return '<figure class="ql-modal-thumbnail ql-modal-thumbnail-loader"></figure>';
  }

  getModalContentTemplate() {
    return `
      <div class="ql-modal-header">
        <h5 class="ql-modal-title">${t('Add Image')}</h5>
        <button class="ql-modal-close"></button>
      </div>
      <div class="ql-modal-body">
        <div class="ql-modal-group">
          <label class="ql-modal-label" for="ql-modal-input-1">${t('Image URL:')}</label>
          <input class="ql-modal-input" type="text" id="ql-modal-input-1">
        </div>
        <div class="ql-modal-drop">
          <button class="ql-modal-btn-dropzone" type="button">${t('Drop an image or click to select')}</button>
          <input class="ql-modal-input-file"  type="file" name="imageFiles" multiple>
        </div>
        <div class="ql-modal-group ql-modal-group-thumbnails"></div>
        <ul class="ql-modal-errors"></ul>
      </div>
      <div class="ql-modal-footer">
        <button class="ql-modal-btn ql-modal-btn-primary ql-modal-add" type="button">${t('Add')}</button>
        <button class="ql-modal-btn ql-modal-btn-secondary ql-modal-cancel" type="button">${t('Cancel')}</button>
      </div>`;
  }
}

export default JotformImage;
